import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { CheckSquare, Copy, Lightbulb, Plus, Search, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNotes } from "./use-notes";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { toast } from "sonner";
import { DrawerClose } from "../../../../components/ui/drawer";
import { useTranslation } from "react-i18next";

const TakeawaysRunningActivity = ({
  processId,
  isMobile,
}: {
  processId: string;
  isMobile: boolean;
}) => {
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(
    null
  );
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);
  const [newNoteType, setNewNoteType] = useState<"action" | "insight">(
    "action"
  );
  const [newNoteContent, setNewNoteContent] = useState("");
  const [newNoteAssignee, setNewNoteAssignee] = useState("current-user");
  const [searchQuery, setSearchQuery] = useState("");
  // const [activeFilter, setActiveFilter] = useState<any>("all");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const { t } = useTranslation();

  const { notes, loading, error, addNote, editNote, removeNote } = useNotes({
    processId,
    // activeFilter,
    searchQuery: debouncedQuery,
    onError: (error: any) => {
      toast(error);
    },
    onSuccess: (message: any) => {
      toast(message);
    },
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const toggleNoteType = async (index: number) => {
    const note = notes[index];
    if (!note._id) return;

    const newType = note.type === "action" ? "insight" : "action";
    await editNote(note._id, { type: newType });
  };

  const saveNoteContent = async (index: number, content: string) => {
    const note = notes[index];
    if (!note._id) return;

    if (content.trim() === "") {
      await removeNote(note._id);
    } else {
      await editNote(note._id, { content });
    }
  };

  const deleteTakeAway = async (index: number) => {
    const note = notes[index];
    if (!note._id) return;

    await removeNote(note._id);
    if (selectedNoteIndex === index) {
      setSelectedNoteIndex(null);
    } else if (selectedNoteIndex !== null && selectedNoteIndex > index) {
      setSelectedNoteIndex(selectedNoteIndex - 1);
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim()) return;

    const success = await addNote({
      type: newNoteType,
      content: newNoteContent,
      assignedTo: newNoteAssignee,
      createdBy: "current-user",
    });

    if (success) {
      setNewNoteContent("");
      setShowNewNoteForm(false);
    }
  };

  const renderNotesList = (
    notesList: any[],
    title: string,
    icon: React.ReactNode,
    emptyMessage: string,
    isActionItem: boolean
  ) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="text-sm font-medium">{title}</h4>
          <span className="text-xs text-muted-foreground">
            ({notesList.length})
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            if (isActionItem) {
              setNewNoteType("action");
            } else {
              setNewNoteType("insight");
            }
            setShowNewNoteForm(true);
          }}
          disabled={loading}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("add")}
        </Button>
      </div>

      <div className="space-y-2 max-h-[9rem] overflow-y-auto pr-1">
        {notesList.map((note: any) => {
          const noteIndex = notes.findIndex((n) => n._id === note._id);
          return (
            <div
              key={note._id}
              className="flex items-center gap-2 p-2 rounded-md border group hover:bg-muted/50 overflow-x-auto"
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 mt-1"
                onClick={() => toggleNoteType(noteIndex)}
                title={note.type === "action" ? "Action Item" : "Insight"}
                disabled={loading}
              >
                {note.type === "action" ? (
                  <CheckSquare className="h-4 w-4 text-blue-500" />
                ) : (
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                )}
              </Button>

              <div className="flex-1 space-y-2">
                {selectedNoteIndex === note._id ? (
                  <Textarea
                    defaultValue={note.content}
                    className="resize-none min-h-[60px]"
                    autoFocus
                    onBlur={(e) => {
                      setSelectedNoteIndex(null);
                      saveNoteContent(noteIndex, e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        setSelectedNoteIndex(null);
                        saveNoteContent(noteIndex, e.currentTarget.value);
                      }
                    }}
                    disabled={loading}
                  />
                ) : (
                  <div
                    className="text-sm cursor-pointer line-clamp-1"
                    onClick={() => setSelectedNoteIndex(note._id)}
                  >
                    {note.content}
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => deleteTakeAway(noteIndex)}
                title="Delete note"
                disabled={loading}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          );
        })}

        {notesList.length === 0 && (
          <div className="text-center py-3 text-muted-foreground">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );

  const copyTakeawaysToClipboard = async () => {
    const actionItems = notes
      .filter(
        (note: any) => note.type === "action" && note.content.trim() !== ""
      )
      .map((note: any) => `- ${note.content}`);

    const insights = notes
      .filter(
        (note: any) => note.type === "insight" && note.content.trim() !== ""
      )
      .map((note: any) => `- ${note.content}`);

    let notesText = "";

    if (actionItems.length > 0) {
      notesText += `📝 ${t("action_items")}:\n${actionItems.join("\n")}\n\n`;
    }

    if (insights.length > 0) {
      notesText += `💡 ${t("insights")}:\n${insights.join("\n")}`;
    }

    if (!notesText.trim()) {
      toast(t("no_notes_copy_message"));
      return;
    }

    try {
      await navigator.clipboard.writeText(notesText.trim());
      toast.info(t("copy_notes_toast_message"));
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast.error("Failed to copy notes");
    }
  };

  const actionNotes = notes.filter((note) => note.type === "action");
  const insightNotes = notes.filter((note) => note.type === "insight");

  return (
    <div className="space-y-4 overflow-y-auto">
      {isMobile && (
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium">{t("your_takeaways")}</h3>
          <DrawerClose>
            <X className="h-4 w-4" />
          </DrawerClose>
        </div>
      )}

      {/* Search Bar */}
      {!showNewNoteForm && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("search_notes")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 focus-visible:ring-0"
          />
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-4 text-muted-foreground">
          <p className="text-sm">{t("loading_notes")}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-4 text-red-500">
          <p className="text-sm">{error}</p>
        </div>
      )}
      {!showNewNoteForm && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={copyTakeawaysToClipboard}
            className="flex items-center gap-2"
          >
            <Copy className="h-4 w-4" />
            {t("copy_all_notes")}
          </Button>

          {/* Actions Section */}
          {renderNotesList(
            actionNotes,
            t("action_items"),
            <CheckSquare className="h-4 w-4 text-blue-500" />,
            t("no_action_items_yet"),
            true
          )}

          {/* Insights Section */}
          {renderNotesList(
            insightNotes,
            t("insights"),
            <Lightbulb className="h-4 w-4 text-amber-500" />,
            t("no_insights_yet"),
            false
          )}
        </>
      )}

      {/* New Note Form */}
      {showNewNoteForm && (
        <div className="border rounded-md p-3 space-y-3 bg-muted/20">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => {
                setNewNoteType(newNoteType === "action" ? "insight" : "action");
              }}
            >
              {newNoteType === "action" ? (
                <CheckSquare className="h-4 w-4 text-blue-500" />
              ) : (
                <Lightbulb className="h-4 w-4 text-amber-500" />
              )}
            </Button>
            <div className="text-sm font-medium">
              {newNoteType === "action" ? t("action_item") : t("insight")}
            </div>
          </div>

          <Textarea
            placeholder={t("new_note_field_placeholder")}
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            className="resize-none min-h-[80px]"
            autoFocus
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowNewNoteForm(false);
                setNewNoteContent("");
              }}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              size="sm"
              onClick={handleCreateNote}
              disabled={!newNoteContent.trim() || loading}
            >
              {loading ? t("saving") : t("save")}
            </Button>
          </div>
        </div>
      )}

      {!showNewNoteForm && (
        <div className="p-3 bg-muted rounded-lg text-sm">
          <p>{t("takeaways_bottom_message")}</p>
        </div>
      )}
    </div>
  );
};

export default TakeawaysRunningActivity;
